import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Icon } from "@iconify/react";
import style from "../../scss/Dashboard.module.scss";
import TablaConteo from "./component/TablaConteo.jsx";
import DashboardCorporacionComponent from "./component/DashboardCorporacionComponent.jsx";
import MesasProblemaComponent from "./component/MesasProblemaComponent.jsx";
import { filtrovotos } from "../../Api/ApiConteo.js";
import { getEscrutinios } from "../../Api/ApiEscrutinio";
import SelectComponent from "../../components/SelectComponent";
import useCatalogos from "../../hooks/useCatalogos";
import { calcularTotalesConteo } from "../../utils/totalesConteo";
import { mesasConProblemas, rutaMesas } from "../../utils/problemasMesa";
import { esAdmin } from "../../utils/sesion";
import useAutoRefresco from "../../hooks/useAutoRefresco";

const ResultadoConteoPages = () => {
  const { candidatos, opcionesCandidato, opcionesLugar, opcionesMesaDe } = useCatalogos();
  const [conteo, setconteo] = useState([]);
  const [escrutinios, setEscrutinios] = useState([]);
  const [selectedOptionlugar, setSelectedOptionlugar] = useState(null);
  const [selectedOptionmesa, setSelectedOptionmesa] = useState(null);
  const [selectecandidato, setSelectecandidato] = useState(null);
  const [actualizado, setActualizado] = useState(null);

  const filtrosr = () => {
    filtrovotos({
      candidatoId: selectecandidato,
      lugar: selectedOptionlugar,
      mesa: selectedOptionmesa,
    })
      .then((response) => {
        if (Array.isArray(response)) {
          setconteo(response);
          setActualizado(new Date());
        }
      })
      .catch((error) => console.error(error));

    // actas E-14: votos en blanco, nulos, no marcados y estado de las mesas
    getEscrutinios({ lugar: selectedOptionlugar, mesa: selectedOptionmesa })
      .then((data) => setEscrutinios(data.escrutinios ?? []))
      .catch((error) => console.error(error));
  };

  //metodo de filtro
  useEffect(() => {
    filtrosr();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectecandidato, selectedOptionlugar, selectedOptionmesa]);

  // el dashboard se actualiza solo con lo que registren otros usuarios
  useAutoRefresco(filtrosr);

  const resumenCorporaciones = calcularTotalesConteo(conteo, candidatos, escrutinios);
  const mesasProblema = useMemo(() => mesasConProblemas(escrutinios), [escrutinios]);

  // cada indicador lleva a la lista de esas mesas (respetando lugar y mesa filtrados)
  const filtrosActivos = { lugar: selectedOptionlugar, mesa: selectedOptionmesa };
  const indicadoresGenerales = [
    { filtro: "escrutadas", etiqueta: "Mesas escrutadas", valor: escrutinios.length, icono: "mdi:table-large" },
    {
      filtro: "cerradas",
      etiqueta: "Mesas cerradas",
      valor: escrutinios.filter((e) => e.estado === "cerrado").length,
      icono: "mdi:lock-check-outline",
    },
    {
      filtro: "problemas",
      etiqueta: "Mesas con problemas",
      valor: mesasProblema.length,
      icono: "mdi:alert-outline",
      alerta: true,
    },
    {
      filtro: "sinFoto",
      etiqueta: "Actas sin foto",
      valor: escrutinios.filter((e) => !e.tieneFoto).length,
      icono: "mdi:camera-off-outline",
    },
  ];

  return (
    <div className={`container ${style.dashboard}`}>
      <div className={style.encabezado}>
        <div>
          <h1 className={style.titulo}>Resultados del escrutinio</h1>
          <p className={style.descripcion}>
            {actualizado
              ? `Actualizado a las ${actualizado.toLocaleTimeString("es-CO", { hour: "2-digit", minute: "2-digit" })} · se refresca cada 30 s`
              : "Cargando resultados…"}
          </p>
        </div>
        {!esAdmin() && <p className={style.aviso}>Solo se muestran las mesas que tú registraste.</p>}
      </div>

      <div className={`row g-3 ${style.filtros}`}>
        <div className="col-12 col-md-4">
          <SelectComponent
            etiqueta="Candidato"
            opciones={opcionesCandidato}
            claseEtiqueta="texto-en-negrilla"
            onCambiar={setSelectecandidato}
            valor={selectecandidato}
          />
        </div>
        <div className="col-12 col-md-4">
          <SelectComponent
            etiqueta="Lugar"
            opciones={opcionesLugar}
            claseEtiqueta="texto-en-negrilla"
            onCambiar={(valor) => {
              // las mesas dependen del lugar: al cambiarlo se quita el filtro de mesa
              setSelectedOptionlugar(valor);
              setSelectedOptionmesa(null);
            }}
            valor={selectedOptionlugar}
          />
        </div>
        <div className="col-12 col-md-4">
          <SelectComponent
            etiqueta="Mesa"
            opciones={opcionesMesaDe(selectedOptionlugar)}
            claseEtiqueta="texto-en-negrilla"
            onCambiar={setSelectedOptionmesa}
            valor={selectedOptionmesa}
          />
        </div>
      </div>

      <ul className={style.generales}>
        {indicadoresGenerales.map((indicador) => (
          <li key={indicador.filtro}>
            <Link
              to={rutaMesas(indicador.filtro, filtrosActivos)}
              className={`${style.general} ${indicador.alerta && indicador.valor > 0 ? style.generalAlerta : ""}`}
              title={`Ver ${indicador.etiqueta.toLowerCase()}`}
            >
              <span className={style.generalEtiqueta}>
                <Icon icon={indicador.icono} aria-hidden="true" />
                {indicador.etiqueta}
              </span>
              <span className={style.generalValor}>{indicador.valor.toLocaleString("es-CO")}</span>
              <span className={style.generalAccion}>
                Ver mesas <Icon icon="mdi:arrow-right" aria-hidden="true" />
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <div className="row g-4">
        {resumenCorporaciones.map((resumen) => (
          <div className="col-12 col-xl-6" key={resumen.corporacion}>
            <DashboardCorporacionComponent resumen={resumen} />
          </div>
        ))}
      </div>


      <section className={style.panel} aria-labelledby="titulo-detalle">
        <div className={style.cabecera}>
          <h2 id="titulo-detalle" className={style.corporacion}>
            Detalle de votos por mesa
          </h2>
          <span className={style.chip}>
            <Icon icon="mdi:table-large" aria-hidden="true" />
            {conteo.length} {conteo.length === 1 ? "registro" : "registros"}
          </span>
        </div>
        <TablaConteo
          datos={conteo}
          setconteo={setconteo}
          onCambio={filtrosr}
          opcionesCandidato={opcionesCandidato}
          opcionesLugar={opcionesLugar}
          opcionesMesaDe={opcionesMesaDe}
        />
      </section>
    </div>
  );
};
export default ResultadoConteoPages;
